// "Has anyone typed into this dialog since it opened?", for the Escape handling shared by
// Widgets.Modal (Sprockets) and Modal.jsx (webpack). Both are asking about a DOM subtree, so one
// implementation serves both; the packs entry point puts it on `window` for the Sprockets side, the
// way it already does for I18n and luxon.
//
// The snapshot lives in a WeakMap keyed by the form element rather than on the element itself:
// nothing to clean up when the modal's content is replaced, and nothing for a serializer or a
// `data-` reader elsewhere to trip over.
const snapshots = new WeakMap();
const watchers = new WeakMap();

const CONTROLS = "input, select, textarea";

// Read the controls rather than handing the form to FormData. FormData reports only *successful*
// controls -- ones carrying a `name` -- and the React fields in these dialogs do not all have one:
// `RadioButton` renders a bare `<input type="radio">` and keeps the selection in React state, so a
// FormData snapshot is unchanged by picking one and Escape would discard it without asking.
//
// Disabled controls stay out: one the user cannot reach is not input they would mind losing. A file
// input serialises to its filename, which is coarse, but picking a file is picking a file and it
// only ever errs towards asking.
const valueOf = (control) => {
  if (control.type === "checkbox" || control.type === "radio") {
    return String(control.checked);
  }

  // `select.value` is the *first* selected option and nothing else, so a multiple select whose
  // first option stays put serialises identically however the rest of it is changed -- picking a
  // second team, or dropping one, would have read as untouched and been discarded without asking.
  if (control.multiple && control.selectedOptions) {
    return Array.from(control.selectedOptions)
      .map((option) => option.value)
      .join("\u0002");
  }

  return control.value;
};

// Position-prefixed, so that a form which has gained or lost a control since the snapshot reads as
// changed even when the controls it kept still hold their old values. Unnamed controls are not
// otherwise distinguishable from one another.
const serialize = (form) =>
  Array.from(form.querySelectorAll(CONTROLS))
    .filter((control) => !control.disabled)
    .map(
      (control, index) =>
        `${index}\u0000${control.name}\u0000${valueOf(control)}`,
    )
    .join("\u0001");

const formsIn = (container) =>
  container ? Array.from(container.querySelectorAll("form")) : [];

const snapshot = (container) => {
  formsIn(container).forEach((form) => snapshots.set(form, serialize(form)));
};

// The fields a `react_component` draws are not in the HTML the modal inserts -- there is a
// placeholder div where the control will be, and React fills it in when it commits, which is a task
// or more later and on a schedule nothing here can read. A snapshot taken at any fixed moment is
// therefore of a form that then grows inputs, and every dialog with a react-select in it read as
// dirty before anyone had touched it.
//
// So rather than guess at a delay: keep re-taking it, and stop at the first sign of a person. After
// that the changes are theirs, and snapshotting one away is how Escape would discard a pick made
// from a react-select -- which mutates the DOM exactly as mounting does, and is indistinguishable
// from it by any other means.
//
// `pointerdown`/`keydown` rather than `input`/`change`: choosing an option sets the value of a
// hidden input from React state, which fires neither.
//
// A MutationObserver does not see `value` or `checked` being assigned, which is how React writes a
// controlled field -- but it does not have to. Any mutation in the subtree wakes it, and what it
// does then is re-serialize every control from its live properties, so a property write alongside
// one is picked up. A render that assigns properties and touches no node at all would be missed;
// the cost of that is a confirm nobody needed, which is the direction this errs in anyway.
const INTERACTIONS = ["pointerdown", "keydown"];

const stopWatching = (container) => {
  const watcher = watchers.get(container);
  if (!watcher) return;

  watcher.observer.disconnect();
  INTERACTIONS.forEach((name) =>
    container.removeEventListener(name, watcher.onInteraction, true),
  );
  watchers.delete(container);
};

const watchUntilTouched = (container) => {
  stopWatching(container);

  const observer = new MutationObserver(() => snapshot(container));
  const onInteraction = () => stopWatching(container);

  watchers.set(container, { observer, onInteraction });
  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: true,
  });
  // Captured, so that a handler inside the dialog cannot stop this from being the last word.
  INTERACTIONS.forEach((name) =>
    container.addEventListener(name, onInteraction, true),
  );
};

export const rememberFormState = (container) => {
  if (!container) return;

  snapshot(container);
  watchUntilTouched(container);
};

// The watcher stops on its own at the first sign of a person, and a container that goes out of the
// document takes its observer and its listeners with it -- the observer is reachable only from the
// node it observes, and `watchers` holds the node weakly. This is for the case in between: a dialog
// closed without being touched, whose watcher would otherwise sit on detached content until the
// collector got to it.
export const forgetFormState = (container) => {
  if (container) stopWatching(container);
};

export const hasUnsavedInput = (container) =>
  formsIn(container).some((form) => {
    const opened = snapshots.get(form);

    // A form nobody snapshotted appeared after the dialog opened. Rather than guess what is in it,
    // treat it as unsaved: the cost of being wrong is one confirm too many, not lost work.
    return opened === undefined || opened !== serialize(form);
  });
